import { useCallback, useEffect, useState } from "react";
import { toast } from "../../lib/notify";
import { http, toastApiError } from "../../lib/api";
import { modalBudgetFrom, modalLinesFrom, modalTotals, rateOrNull } from "../../lib/invoiceCalc";

// satır doğrulama: KDV zorunlu; hakedişte ana+alt bütçe zorunlu; ana/alt birlikte seçilmeli
function lineError(l, i, isClaim) {
  if (l.kdv === "" || l.kdv == null) return `${i + 1}. satır: KDV oranını seçiniz`;
  if (isClaim && (!l.parent || !l.item)) return `${i + 1}. satır: ana ve alt bütçe kalemini seçin`;
  if (!!l.parent !== !!l.item) return `${i + 1}. satır: ana ve alt bütçe kalemini birlikte seçin`;
  return null;
}

// fatura önizleme / düzenleme modalı: satırlar, bütçe-KDV seçimleri, kaydet, onay iptali, kapatma (ESC + geri dönüş yolu)
export function useInvoiceModal({ load, navigate, incomeBudget }) {
  const [viewing, setViewing] = useState(null);
  const [returnTo, setReturnTo] = useState(null);
  const [modalBudget, setModalBudget] = useState({ parent: "", item: "" });
  const [, setModalErrors] = useState({});
  const [modalLines, setModalLines] = useState([]);

  const closeModal = useCallback(() => { setViewing(null); setModalErrors({}); if (returnTo) { const t = returnTo; setReturnTo(null); navigate(t); } }, [returnTo, navigate]);
  useEffect(() => {
    if (!viewing) return;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); closeModal(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [viewing, closeModal]);

  const openModal = (inv) => {
    setModalErrors({});
    setViewing(inv);
    setModalLines(modalLinesFrom(inv, incomeBudget));
    setModalBudget(modalBudgetFrom(inv));
  };
  const changeModalLine = (i, patch) => setModalLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const applyLineToAll = (i) => setModalLines((ls) => ls.map((l) => ({ ...l, parent: ls[i].parent, item: ls[i].item, kdv: ls[i].kdv, tevkifat: ls[i].tevkifat })));

  const saveModalLines = async () => {
    if (!viewing) return;
    for (let i = 0; i < modalLines.length; i++) {
      const err = lineError(modalLines[i], i, !!viewing.claim);
      if (err) { toast.error(err); return; }
    }
    try {
      const { data } = await http.put(`/invoices/${viewing.id}/lines`, {
        invoice_no: modalBudget.fatura_no ?? viewing.invoice_no ?? "",
        invoice_date: modalBudget.fatura_tarihi || viewing.invoice_date,
        due_date: modalBudget.odeme_tarihi || viewing.due_date,
        lines: modalLines.map((l) => ({
          description: l.description, unit: l.unit, quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0,
          budget_parent_id: l.parent || null, budget_item_id: l.item || null,
          kdv_rate: parseFloat(l.kdv), tevkifat_rate: rateOrNull(l.tevkifat),
        })),
      });
      const wasDraft = viewing.claim && !viewing.budget_item_id;
      if (wasDraft) toast.success("Fatura onaylandı — Onayı iptal etmek için listeden 'Onaylı' rozetine tıklayıp açılan pencerede 'Onayı İptal Et' butonunu kullanın.", { duration: 8000 });
      else toast.success(data.count > 1 ? `Kaydedildi — farklı bütçe/KDV seçimleri ${data.count} ayrı kayda bölündü` : "Fatura güncellendi — bütçe raporlarına yansıdı");
      await load();
      setViewing(null);
      setModalErrors({});
      if (returnTo) { const t = returnTo; setReturnTo(null); navigate(t); }
    } catch (err) { toastApiError(err); }
  };

  const unapproveInvoice = async () => {
    if (!viewing) return;
    if (!window.confirm("Onay iptal edilecek ve fatura Taslak durumuna dönecek (ödeme planı ve bütçeden çıkar). Devam edilsin mi?")) return;
    try {
      await http.patch(`/invoices/${viewing.id}/unapprove`);
      toast.success("Onay iptal edildi — fatura Taslak durumuna alındı");
      await load();
      setViewing(null);
      if (returnTo) { const t = returnTo; setReturnTo(null); navigate(t); }
    } catch (err) { toastApiError(err); }
  };

  return { viewing, setViewing, closeModal, openModal, setReturnTo, modalLines, changeModalLine, applyLineToAll, saveModalLines, unapproveInvoice, modalBudget, setModalBudget, ...modalTotals(modalLines) };
}
